import { ApiError } from '../api/http.ts';

interface ValidationProblem {
  title?: string;
  detail?: string;
  errors?: Record<string, string[]>;
}

/**
 * Turns an API failure into one sentence a person can act on.
 *
 * ASP.NET Core validation problems arrive keyed by Identity's error codes
 * (`DuplicateUserName`, `PasswordTooShort`, …). The raw descriptions are serviceable but
 * occasionally leak framework vocabulary, so the ones people actually hit are reworded.
 */
export function describeError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Something went wrong. Please try again.';
  }

  if (error.status === 401) {
    return 'That email and password combination was not recognised.';
  }

  if (error.status >= 500) {
    return 'The server is having a moment. Please try again shortly.';
  }

  const problem = error.body as ValidationProblem | null;
  const codes = Object.keys(problem?.errors ?? {});

  for (const code of codes) {
    const friendly = friendlyMessages[code];
    if (friendly) {
      return friendly;
    }
  }

  const firstDescription = Object.values(problem?.errors ?? {})[0]?.[0];
  return firstDescription ?? problem?.detail ?? problem?.title ?? 'That did not work.';
}

const friendlyMessages: Record<string, string> = {
  DuplicateUserName: 'There is already an account with that email address.',
  DuplicateEmail: 'There is already an account with that email address.',
  PasswordTooShort: 'Passwords need to be at least 10 characters.',
  PasswordRequiresUniqueChars: 'Please use a few more different characters.',
  InvalidEmail: 'That does not look like an email address.',
  DisplayName: 'Please enter a name to show to the rest of your group.',
};
